import { createSlice } from '@reduxjs/toolkit';
import {
  fetchStoreOwnersThunk,
  fetchStoreOwnerThunk,
  createStoreOwnerThunk,
  updateStoreOwnerThunk,
  deleteStoreOwnerThunk,
  activateStoreOwnerThunk,
  deactivateStoreOwnerThunk,
} from './storeOwnerThunks';

const initialState = {
  storeOwners: [],
  currentStoreOwner: null,
  loading: false,
  error: null,
};

const storeOwnerSlice = createSlice({
  name: 'storeOwners',
  initialState,
  reducers: {
    clearCurrentStoreOwner(state) {
      state.currentStoreOwner = null;
    },
    clearStoreOwnerErrors(state) {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchAll
      .addCase(fetchStoreOwnersThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStoreOwnersThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.storeOwners = action.payload;
      })
      .addCase(fetchStoreOwnersThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // fetchOne
      .addCase(fetchStoreOwnerThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStoreOwnerThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.currentStoreOwner = action.payload;
      })
      .addCase(fetchStoreOwnerThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // create
      .addCase(createStoreOwnerThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createStoreOwnerThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.storeOwners.push(action.payload);
      })
      .addCase(createStoreOwnerThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // update
      .addCase(updateStoreOwnerThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateStoreOwnerThunk.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        const idx = state.storeOwners.findIndex((owner) => owner.id === updated.id);
        if (idx !== -1) {
          state.storeOwners[idx] = updated;
        }
        if (state.currentStoreOwner && state.currentStoreOwner.id === updated.id) {
          state.currentStoreOwner = updated;
        }
      })
      .addCase(updateStoreOwnerThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // delete
      .addCase(deleteStoreOwnerThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteStoreOwnerThunk.fulfilled, (state, action) => {
        state.loading = false;
        const deletedId = action.payload;
        state.storeOwners = state.storeOwners.filter(owner => owner.id !== deletedId);
        if (state.currentStoreOwner && state.currentStoreOwner.id === deletedId) {
          state.currentStoreOwner = null;
        }
      })
      .addCase(deleteStoreOwnerThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // deactivate store owner
      .addCase(deactivateStoreOwnerThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deactivateStoreOwnerThunk.fulfilled, (state, action) => {
        state.loading = false;
        const ownerId = action.payload;
        const idx = state.storeOwners.findIndex((o) => o.id === ownerId);
        if (idx !== -1) {
          state.storeOwners[idx] = { ...state.storeOwners[idx], is_active: false };
        }
        if (state.currentStoreOwner && state.currentStoreOwner.id === ownerId) {
          state.currentStoreOwner = { ...state.currentStoreOwner, is_active: false };
        }
      })
      .addCase(deactivateStoreOwnerThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // activate store owner
      .addCase(activateStoreOwnerThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(activateStoreOwnerThunk.fulfilled, (state, action) => {
        state.loading = false;
        const ownerId = action.payload;
        const idx = state.storeOwners.findIndex((o) => o.id === ownerId);
        if (idx !== -1) {
          state.storeOwners[idx] = { ...state.storeOwners[idx], is_active: true };
        }
        if (state.currentStoreOwner && state.currentStoreOwner.id === ownerId) {
          state.currentStoreOwner = { ...state.currentStoreOwner, is_active: true };
        }
      })
      .addCase(activateStoreOwnerThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearCurrentStoreOwner, clearStoreOwnerErrors } = storeOwnerSlice.actions;
export default storeOwnerSlice.reducer;